import React from "react"
import { useState } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Typography,
  useTheme,
  Switch,
  FormControlLabel,
  Divider,
} from "@mui/material"

const PaginationSettingsModal = ({
  open,
  onClose,
  currentSettings,
  onSettingsUpdate,
}) => {
  const theme = useTheme()
  const [settings, setSettings] = useState(currentSettings)

  const rowsPerPageOptions = [5, 10, 15, 20, 25, 50, 100]

  const handleSettingChange = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleSave = () => {
    onSettingsUpdate(settings)
    onClose()
  }

  const handleReset = () => {
    const defaultSettings = {
      rowsPerPage: 20,
      showPageInfo: true,
      showFirstLast: true,
      showRowsPerPageSelector: false,
    }
    setSettings(defaultSettings)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.text.color,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Pagination Settings</Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: theme.palette.secondaryText.color }}>Rows Per Page</InputLabel>
            <Select
              value={settings.rowsPerPage}
              onChange={handleSettingChange("rowsPerPage")}
              label="Rows Per Page"
              sx={{
                color: theme.palette.text.color,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.border.color,
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.primary.main,
                },
                "& .MuiSvgIcon-root": {
                  color: theme.palette.text.color,
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: theme.palette.secondary.main,
                    color: theme.palette.text.color,
                  },
                },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  sx={{
                    color: theme.palette.text.color,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main + "20",
                    },
                  }}
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ backgroundColor: theme.palette.border.color }} />

          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Display Options
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={settings.showPageInfo}
                onChange={handleSettingChange("showPageInfo")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Show Page Information</Typography>
                <Typography variant="body2" sx={{ color: theme.palette.secondaryText.color }}>
                  Display "Page X of Y" information
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.showFirstLast}
                onChange={handleSettingChange("showFirstLast")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Show First/Last Buttons</Typography>
                <Typography variant="body2" sx={{ color: theme.palette.secondaryText.color }}>
                  Display first and last page navigation buttons
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.showRowsPerPageSelector}
                onChange={handleSettingChange("showRowsPerPageSelector")}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: theme.palette.primary.main,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1">Show Rows Per Page Selector</Typography>
                <Typography variant="body2" sx={{ color: theme.palette.secondaryText.color }}>
                  Allow users to change rows per page dynamically
                </Typography>
              </Box>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={handleReset} variant='contained' color='info'>
          Reset to Default
        </Button>
        <Button onClick={onClose} variant="contained" color="error">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            backgroundColor: theme.palette.primary.main,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PaginationSettingsModal
